import { useEffect, useRef, useState } from 'react';
import axios from '../utils/axios';
import type {
  BaseTemplate,
  BaseTemplateMarket,
  BaseTemplateResponse,
} from '../types/baseTemplates';

const positiveNumber = (value: unknown): number | null => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : null;
};

const mapMarket = (market: any): BaseTemplateMarket | null => {
  const id = positiveNumber(market?.id);
  if (!id) return null;
  return {
    id,
    name: String(market?.name || market?.label || market?.code || ''),
    ...(market?.code ? { code: String(market.code) } : {}),
    ...(typeof market?.hasLicense === 'boolean' ? { hasLicense: market.hasLicense } : {}),
  };
};

export const mapBaseTemplate = (template: any): BaseTemplate | null => {
  const id = positiveNumber(template?.id);
  if (!id) return null;
  const markets = (Array.isArray(template?.markets) ? template.markets : [])
    .map(mapMarket)
    .filter((market): market is BaseTemplateMarket => market !== null);
  const marketIds = (Array.isArray(template?.marketIds)
    ? template.marketIds
    : markets.map((market) => market.id))
    .map(positiveNumber)
    .filter((marketId): marketId is number => marketId !== null);

  return {
    id,
    name: String(template?.name || ''),
    content: typeof template?.content === 'string' ? template.content : undefined,
    data: template?.data,
    category: String(template?.category || ''),
    width: Number(template?.width) || 0,
    height: Number(template?.height) || 0,
    unit: String(template?.unit || ''),
    qrCompatibility: String(template?.qrCompatibility).toLowerCase() === 'mqr' ? 'mqr' : 'qr',
    previewUrl: String(template?.previewUrl || template?.previewReference || ''),
    marketIds,
    markets,
  };
};

export async function getBaseTemplates(
  organizationId: number | string,
): Promise<BaseTemplateResponse> {
  const response = await axios.get('/template/base', { params: { organizationId } });
  const payload = response.data || {};
  const templates = (Array.isArray(payload.templates) ? payload.templates : [])
    .map(mapBaseTemplate)
    .filter((template): template is BaseTemplate => template !== null);
  const accessibleMarkets = (Array.isArray(payload.accessibleMarkets)
    ? payload.accessibleMarkets
    : [])
    .map(mapMarket)
    .filter((market): market is BaseTemplateMarket => market !== null);

  return {
    templates,
    accessibleMarkets,
    defaultMarketId: positiveNumber(payload.defaultMarketId),
  };
}

export async function getBaseTemplateById({
  organizationId,
  templateId,
}: {
  organizationId: number | string;
  templateId: number | string;
}): Promise<BaseTemplate> {
  const response = await axios.get('/template/base/byid', {
    params: { organizationId, templateId },
  });
  const template = mapBaseTemplate(response.data);
  if (!template) throw new Error('The selected Base Template response was invalid.');
  return template;
}

export default function useBaseTemplates(
  organizationId: number | string | null,
  userId?: number | string | null,
) {
  const [data, setData] = useState<BaseTemplateResponse>({
    templates: [],
    accessibleMarkets: [],
    defaultMarketId: null,
  });
  const [isLoading, setIsLoading] = useState(Boolean(organizationId));
  const [error, setError] = useState<unknown>(null);
  const requestId = useRef(0);

  const load = async () => {
    const currentRequest = ++requestId.current;
    setData({ templates: [], accessibleMarkets: [], defaultMarketId: null });
    setError(null);
    if (!organizationId) {
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    try {
      const result = await getBaseTemplates(organizationId);
      if (currentRequest === requestId.current) setData(result);
    } catch (nextError) {
      if (currentRequest === requestId.current) setError(nextError);
    } finally {
      if (currentRequest === requestId.current) setIsLoading(false);
    }
  };

  useEffect(() => {
    load().catch(() => null);
    return () => {
      requestId.current += 1;
    };
  }, [organizationId, userId]);

  return { data, error, isLoading, refetch: load };
}
